import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { Send, UserPlus, PhoneOff } from 'lucide-react'

const STATUS_META: Record<string, { label: string; classes: string }> = {
  waiting: { label: 'Waiting', classes: 'bg-amber-100 text-amber-700' },
  active:  { label: 'Active',  classes: 'bg-green-100 text-green-700' },
  closed:  { label: 'Closed',  classes: 'bg-muted text-muted-foreground' },
}

interface Props {
  platformRole: string
}

export function LiveChatView({ platformRole }: Props) {
  const qc = useQueryClient()
  const [filter, setFilter] = useState<'waiting' | 'active' | 'closed'>('waiting')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [userId, setUserId] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null))
  }, [])

  const { data: sessions } = useQuery({
    queryKey: ['chat-sessions'],
    queryFn: async () => {
      const { data } = await supabase
        .from('live_chat_sessions')
        .select('*, organizations(name)')
        .in('status', ['waiting', 'active', 'closed'])
        .order('created_at', { ascending: false })

      const rows = (data || []) as any[]
      const userIds = [...new Set(rows.map(r => r.org_user_id).filter(Boolean))]
      const agentIds = [...new Set(rows.map(r => r.agent_id).filter(Boolean))]

      const [profilesRes, adminsRes] = await Promise.all([
        userIds.length ? supabase.from('profiles').select('user_id, full_name').in('user_id', userIds) : Promise.resolve({ data: [] }),
        agentIds.length ? supabase.from('app_admins' as any).select('user_id, first_name, last_name').in('user_id', agentIds) : Promise.resolve({ data: [] as any[] }),
      ])

      const userNameById: Record<string, string> = {}
      ;(profilesRes.data ?? []).forEach((p: any) => { userNameById[p.user_id] = p.full_name ?? 'Unknown' })
      const agentNameById: Record<string, string> = {}
      ;(adminsRes.data ?? []).forEach((a: any) => { agentNameById[a.user_id] = `${a.first_name} ${a.last_name}` })

      return rows.map(r => ({
        ...r,
        userName: userNameById[r.org_user_id] ?? 'Unknown user',
        agentName: r.agent_id ? (agentNameById[r.agent_id] ?? 'Unknown agent') : null,
      }))
    },
    refetchInterval: 15000,
  })

  useEffect(() => {
    const ch = supabase.channel('chat-sessions-watch')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'live_chat_sessions' },
        () => qc.invalidateQueries({ queryKey: ['chat-sessions'] }))
      .subscribe()
    return () => { supabase.removeChannel(ch) }
  }, [qc])

  const filtered = (sessions ?? []).filter(s => s.status === filter)
  const selected = (sessions ?? []).find(s => s.id === selectedId) ?? null

  const { data: messages } = useQuery({
    queryKey: ['chat-messages', selectedId],
    enabled: !!selectedId,
    queryFn: async () => {
      const { data } = await supabase
        .from('live_chat_messages')
        .select('*')
        .eq('session_id', selectedId!)
        .order('created_at', { ascending: true })
      return data ?? []
    }
  })

  useEffect(() => {
    if (!selectedId) return
    const ch = supabase
      .channel(`chat-messages-${selectedId}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'live_chat_messages',
        filter: `session_id=eq.${selectedId}`,
      }, () => qc.invalidateQueries({ queryKey: ['chat-messages', selectedId] }))
      .subscribe()
    return () => { supabase.removeChannel(ch) }
  }, [selectedId, qc])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  async function sendMessage() {
    if (!message.trim() || !selected || !userId) return
    setSending(true)
    try {
      await supabase.from('live_chat_messages').insert({
        session_id: selected.id,
        sender_id: userId,
        sender_type: 'agent',
        content: message.trim(),
      })
      setMessage('')
    } finally {
      setSending(false)
    }
  }

  async function assignToMe() {
    if (!selected || !userId) return
    await supabase
      .from('live_chat_sessions')
      .update({ agent_id: userId, status: 'active', started_at: new Date().toISOString() })
      .eq('id', selected.id)
    qc.invalidateQueries({ queryKey: ['chat-sessions'] })
  }

  async function endChat() {
    if (!selected) return
    await supabase
      .from('live_chat_sessions')
      .update({ status: 'closed', ended_at: new Date().toISOString() })
      .eq('id', selected.id)
    qc.invalidateQueries({ queryKey: ['chat-sessions'] })
  }

  const waitTime = (createdAt: string) => {
    const mins = Math.floor((Date.now() - new Date(createdAt).getTime()) / 60000)
    return mins < 1 ? 'just now' : `${mins}m`
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Live Chat</h1>
        <p className="text-justify text-muted-foreground text-sm mt-1">Real-time support conversations with institution users.</p>
      </div>

      <div className="flex border rounded-lg overflow-hidden" style={{ height: 560 }}>
        {/* Sessions list */}
        <div className="w-[280px] border-r flex flex-col shrink-0">
          <div className="flex border-b shrink-0">
            {(['waiting', 'active', 'closed'] as const).map(s => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={cn(
                  'flex-1 px-2 py-2 text-xs font-medium border-b-2 transition-colors capitalize',
                  filter === s ? 'border-[#C8A84B] text-foreground' : 'border-transparent text-muted-foreground'
                )}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-justify text-sm text-muted-foreground text-center py-8 px-3">No {filter} sessions.</p>
            ) : filtered.map(s => (
              <button
                key={s.id}
                onClick={() => setSelectedId(s.id)}
                className={cn(
                  'w-full text-left px-3 py-2.5 border-b hover:bg-muted/40 transition-colors',
                  selectedId === s.id && 'bg-muted/60',
                  s.status === 'waiting' && 'bg-amber-50/60'
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium truncate">{s.organizations?.name ?? 'Unknown org'}</span>
                  {s.status === 'waiting' && <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0" />}
                </div>
                <div className="text-xs text-muted-foreground truncate">{s.userName}</div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-muted-foreground">{waitTime(s.created_at)}</span>
                  <Badge className={cn('text-[10px]', STATUS_META[s.status]?.classes)}>{STATUS_META[s.status]?.label ?? s.status}</Badge>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Active chat */}
        <div className="flex-1 flex flex-col">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
              Select a session to view the conversation.
            </div>
          ) : (
            <>
              <div className="border-b px-4 py-3 flex items-center justify-between shrink-0">
                <div>
                  <p className="text-justify text-sm font-semibold">{selected.organizations?.name} · {selected.userName}</p>
                  <p className="text-justify text-xs text-muted-foreground">
                    {selected.status === 'closed' ? 'Closed' : `Started ${waitTime(selected.started_at ?? selected.created_at)} ago`}
                    {selected.agentName && ` · Agent: ${selected.agentName}`}
                  </p>
                </div>
                <div className="flex gap-2">
                  {!selected.agent_id && selected.status !== 'closed' && (
                    <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={assignToMe}>
                      <UserPlus className="w-3.5 h-3.5" />
                      Assign to me
                    </Button>
                  )}
                  {selected.status !== 'closed' && (
                    <Button size="sm" variant="destructive" className="gap-1.5 text-xs" onClick={endChat}>
                      <PhoneOff className="w-3.5 h-3.5" />
                      End Chat
                    </Button>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {(messages ?? []).length === 0 ? (
                  <p className="text-justify text-sm text-muted-foreground text-center py-8">No messages yet.</p>
                ) : (messages ?? []).map(m => (
                  <div key={m.id} className={cn('flex', m.sender_type === 'agent' ? 'justify-end' : 'justify-start')}>
                    <div className={cn(
                      'max-w-[70%] rounded-xl px-4 py-2.5 text-sm',
                      m.sender_type === 'agent' ? 'bg-green-600 text-white' : 'bg-muted text-foreground'
                    )}>
                      <p>{m.content}</p>
                      <p className={cn('text-[10px] mt-1', m.sender_type === 'agent' ? 'text-white/60' : 'text-muted-foreground')}>
                        {new Date(m.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {selected.status !== 'closed' && (
                <div className="border-t p-3 flex gap-2 shrink-0">
                  <Textarea
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder="Type a message…"
                    className="min-h-[44px] max-h-32 resize-none text-sm"
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage() }
                    }}
                  />
                  <Button className="self-end shrink-0" onClick={sendMessage} disabled={sending || !message.trim()}>
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
